import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FadeIn from "../../../components/animations/FadeIn";

const Products = () => {

  const stickyIcons = [
    {
      id: 1,
      icon: "/assets/images/Modum1.png",
    },
    {
      id: 2,
      icon: "/assets/images/Modum.png",
    },
    {
      id: 3,
      icon: "/assets/images/Device002.png",
    },
    {
      id: 4,
      icon: "/assets/images/Sensor_Case.png",
    },
    {
      id: 5,
      icon: "/assets/images/P5.png",
    },
    {
      id: 6,
      icon: "/assets/images/Battery_EV.png",
    },
  ];

  // Split the 6 icons into groups of 3 that will rotate on the banner
  const ICONS_PER_GROUP = 3;
  const iconGroups = [];
  for (let i = 0; i < stickyIcons.length; i += ICONS_PER_GROUP) {
    iconGroups.push(stickyIcons.slice(i, i + ICONS_PER_GROUP));
  }

  const [groupIndex, setGroupIndex] = useState(0);
  const [isZooming, setIsZooming] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      // Step 1: trigger zoom-out/fade transition
      setIsZooming(true);

      // Step 2: after the zoom-out finishes, swap to the next group and zoom back in
      setTimeout(() => {
        setGroupIndex((prev) => (prev + 1) % iconGroups.length);
        setIsZooming(false);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, [iconGroups.length]);

  const sections = [
    {
      id: 1,
      label: "4G Eco9",
      heading: "VEHICLE TRACKING & SECURITY",
      description:
        "Advanced 4G LTE GPS tracker with MOSFET-based relay control designed specifically for 2-wheelers. Features ultra-low sleep current (<8mA) to prevent battery discharge, accelerometer-based vibration detection for unauthorized movement, and Bluetooth keyless ignition control even in no-network zones.",
      image: "/assets/images/P3.png",
      slug: "sensor-network",
    },
    {
      id: 2,
      label: "SMART TRACK",
      heading: "4G/2G Tracker with RHT",
      description:
        "4G Basic Tracking Device with wide voltage range (8V–100V DC), Jio & e-SIM support, Bluetooth console, and environmental monitoring for logistics asset tracking.",
      image: "/assets/images/P4.png",
      slug: "smart-track",
    },
    {
      id: 3,
      label: "BLE Container Lock",
      heading: "Motorized Smart Lock for Containers",
      description:
        "BLE Container Lock with motorized locking mechanism, wide voltage range (10V–100V DC), ultra-low standby current (<10mA), BLE connectivity, and tamper detection for secure container monitoring.",
      image: "/assets/images/P5.png",
      slug: "ble-network",
    },
    {
    id: 4,
    label: "E-VAHAN TRACKER",
    heading: "4G EV Fleet Tracker with BLE Control",
    description:
      "4G based advanced tracker designed specifically for electric vehicles (EV) and fleet management. Features BLE mobile app based configuration, keyless ON/OFF control, and MOSFET based relay-less vehicle ON/OFF. Includes remote battery enable/disable with auto locking, UART based BMS monitoring, and optional dragging auto WheelJam. Ideal for EV fleet management with BLE based battery inventory support.",
    image: "/assets/images/P6.png",
    slug: "e-vahan-tracker",
   },
   {
    id: 5,
    label: "Wireless 3 Phase Ac Detection",
    heading: "Wireless 3 Phase AC Detection Sensor",
    description:
      "Wireless 3 Phase AC (Alternating Current) detection sensor based on frequency range between 45Hz to 55Hz. Ideal for industrial warehouses, cold chain, refrigerator containers, power generator 3-phase existence detection, and outdoor 3-phase AC generator monitoring. Features long-distance wireless transfer with ultra-low power consumption.",
    image: "/assets/images/Modum1.png",
    slug: "3-phase-ac-detection",
   },
  {
    id: 6,
    label: "E-RUTU TRACKER",
    heading: "4G/2G Tracker with RHT & Door Sensor",
    description:
      "4G/2G based tracker with RHT (Relative Humidity & Temperature) and Door sensor for Vehicle and Warehouse monitoring. Supports up to 4 SiWi made SWRT RHT + Door sensors via BLE. Features BLE based Genset ON/OFF monitoring for Reefer and Warehouse applications. Configurable via BLE mobile app with customizable settings. Includes up to 90,000 offline data storage in case of no network connectivity.",
    image: "/assets/images/P6.png",
    slug: "E-rutu-tracker",
  },
   {
    id: 7,
    label: "BLE RHT SENSOR",
    heading: "Wireless Temperature & Humidity (SWRT)",
    description:
      "SiWi BLE (Bluetooth) based Wireless Temperature & Humidity Sensor with BLE 5.1 connectivity. Features wide temperature range from -40°C to +80°C and humidity range from 0% to 99.99%. Includes one digital input for door switch connectivity. Configurable and interfaceable with SiWi 4G IOT devices and Teltonika GPS trackers. Real-time display for temperature and humidity with SiWi RHT application.",
    image: "/assets/images/Device002.png",
    slug: "ble-rht-sensor",
  },
    {
      id: 8,
      label: "BLE ID Card",
      heading: "Smart Identification & Access",
      description: (
        <>
          A Bluetooth Low Energy (BLE) enabled smart ID card designed for secure
          employee identification, contactless access control, attendance
          tracking, and real-time location awareness with{" "}
          <span className="bg-yellow-100 text-gray-700 px-2 py-1 rounded-sm">
            Cheapest CR2032 simple CMOS battery
          </span>
          . Delivering enhanced security, convenience, and seamless connectivity
          for modern workplaces.
        </>
      ),
      image: "/assets/images/BLE.png",
      slug: "ble-id-card",
    },

    {
      id: 9,
      label: "BLE ASSET FINDER",
      heading: "Find Your Assets Instantly",
      description:
        "BLE Asset Finder helps you locate your assets instantly with real-time location tracking. Features built-in buzzer for audible alerts, app-based location monitoring, and BLE 5.0+ connectivity. Designed with ultra-low power consumption for year+ battery life, making it perfect for tracking valuable assets.",
      image: "/assets/images/Sensor_Case.png",
      slug: "ble-asset-finder",
    },
  {
    id: 10,
    label: "EV BATTERY FINDER",
    heading: "Smart Battery Tracking",
    description:
      "EV Battery Finder is a smart battery tracking and anti-theft solution designed for EV 2-wheelers. Features Bluetooth Battery Finder technology for easy battery location, battery inventory management, Smart BMS Lock, and Auto Battery Lock (Walk Away) feature for enhanced security. Perfect for electric vehicle fleet management and individual EV owners.",
    image: "/assets/images/Battery_EV.png",
    slug: "ev-battery-finder",
  },
  {
    id: 10,
    label: "Wireless LR RHT Sensor",
    heading: "RHT Temperature & Humidity Sensor",
    description:
      "Ultra Long Range Bluetooth based Temperature and Humidity Sensor with BLE 5.3 connectivity. Features rechargeable battery with up to 8 months backup, wide temperature range from -40°C to +70°C, and humidity measurement. Equipped with strong antenna providing up to 1200 meters range in open area. High accuracy with ±0.2°C temperature and ±1.8% RH accuracy. Compatible interface with Teltonika GPS tracker devices.",
    image: "/assets/images/Modum.png",
    slug: "ultra-long-range-ble-sensor",
  },
  ];

  const currentIcons = iconGroups[groupIndex] || [];

  return (
    <div className="min-h-screen bg-white">

      {/* HERO SECTION - grey gradient with soft blurred glass blobs, no static image */}
      <div className="relative w-full overflow-hidden bg-gradient-to-br from-gray-200 via-gray-300 to-gray-400 py-24">

        {/* Blurred color blobs for the "glass" mixed look */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-72 h-72 bg-sky-500/30 rounded-full blur-3xl"></div>
        <div className="pointer-events-none absolute top-10 right-0 w-96 h-96 bg-slate-300/50 rounded-full blur-3xl"></div>
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-80 h-80 bg-gray-300/60 rounded-full blur-3xl"></div>

        <div className="relative z-10 flex flex-col items-center justify-center">

          <h1 className="text-4xl font-bold text-gray-800 uppercase mb-16 text-center px-4">
            Advanced IoT Products
          </h1>

          {/* Rotating group of 3 icons - swaps every 5s with a zoom transition */}
          <div className="flex justify-center gap-16 md:gap-24 max-w-4xl w-full px-6 min-h-[220px] items-center overflow-visible">

            {currentIcons.map((icon, index) => (
              <div
                key={icon.id}
                className={`text-center flex flex-col items-center transition-all duration-500 ease-in-out ${
                  isZooming
                    ? "opacity-0 scale-150"
                    : "opacity-100 scale-100"
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >

                <img
                  src={icon.icon}
                  alt={icon.title}
                  className="w-44 h-44 md:w-96 md:h-56 object-contain mx-auto drop-shadow-md scale-150"
                />

                <p className="text-gray-800 mt-4 font-semibold text-sm md:text-base">
                  {icon.title}
                </p>

              </div>
            ))}

          </div>

        </div>

      </div>

      {/* HEADER TEXT */}

      {/* ✅ Header like your style */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[50px] pb-2 text-center">
        <FadeIn>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-medium tracking-tight text-gray-900/80">
            Explore our innovative IoT hardware and tracking products designed to deliver real-time visibility and operational intelligence.          </h2>
        </FadeIn>
      </div>

     

      {/* PRODUCT SECTIONS */}

      {sections.map((product, index) => {

        const reverse = index % 2 !== 0;

        return (

          <div key={product.id} className="max-w-7xl mx-auto py-6 px-6">

            <div
              className={`grid lg:grid-cols-2 gap-10 items-center ${
                reverse ? "lg:flex-row-reverse" : ""
              }`}
            >

              {/* TEXT SECTION */}

              <FadeIn>

                <div>

                  <p className="text-sm font-semibold text-blue-600 uppercase mb-3">
                    {product.label}
                  </p>

                  <h2 className="text-4xl font-light text-gray-900 mb-5">
                    {product.heading}
                  </h2>

                  <p className="text-gray-700 text-lg leading-relaxed">
                    {product.description}
                  </p>

                  <Link
                    to={`/products/${product.slug}`}
                    onClick={() =>
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      })
                    }
                    className="inline-flex items-center mt-6 text-primary-600 font-semibold hover:text-primary-700 transition"
                  >
                    See More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>

                </div>

              </FadeIn>

              {/* IMAGE */}

              <FadeIn>

                <div className="flex justify-center items-center h-[280px] overflow-visible">
                  <img
                    src={product.image}
                    alt={product.heading}
                    className="w-full h-full object-contain scale-150"
                  />
                </div>

              </FadeIn>

            </div>

          </div>

        );

      })}

    </div>
  );
};

export default Products;